// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { CssBaseline, Box } from '@mui/material';
import NotificationAlert from '../shared/components/NotificationAlert';
import HospitalsDataGrid from './components/HospitalsDataGrid.jsx';
import HospitalViewDialog from './components/HospitalViewDialog.jsx';
import AppTheme from '../shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  // datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';
import { useAuth } from '../shared/context/AuthContext';
import SideMenu from '../shared/components/SideMenu';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import { useNavigate } from 'react-router-dom';
import BreadCrumbsWithIcons from '../shared/components/BreadCrumbsWithIcons';
import * as hospitalService from '../services/hospitalService';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  // ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export default function Hospitals(props) {
  const { auth, user, isAdmin, isAuthResolved } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const pageCursorSnapshotsRef = useRef({ 0: null });
  const pendingPageResetRef = useRef(false);
  const latestFetchRequestRef = useRef(0);

  const [viewHospital, setViewHospital] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  
  useEffect(() => {
    if (!isAuthResolved) return;

    if (user) {
      setLoading(true);
      if (!isAdmin) {
        navigate('/dashboard', { replace: true });
      }
    } else {
      navigate('/login', { replace: true });
      setLoading(false);
    }

    setAuthChecked(true);
  }, [isAuthResolved, user, isAdmin, navigate]);

  const activeNameFilterItem = filterModel.items.find((item) => item.field === 'name' && item.value?.trim()) || null;
  const activeEmailFilterItem = filterModel.items.find((item) => item.field === 'contactEmail' && item.value?.trim()) || null;
  const activeNameFilter = activeNameFilterItem?.value?.trim() || '';
  const activeNameFilterOperator = activeNameFilterItem?.operator || 'startsWith';
  const activeEmailFilter = activeEmailFilterItem?.value?.trim() || '';
  const activeEmailFilterOperator = activeEmailFilterItem?.operator || 'startsWith';
  const [debouncedFilters, setDebouncedFilters] = useState({
    nameFilter: activeNameFilter,
    nameFilterOperator: activeNameFilterOperator,
    emailFilter: activeEmailFilter,
    emailFilterOperator: activeEmailFilterOperator,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters({
        nameFilter: activeNameFilter,
        nameFilterOperator: activeNameFilterOperator,
        emailFilter: activeEmailFilter,
        emailFilterOperator: activeEmailFilterOperator,
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [activeNameFilter, activeNameFilterOperator, activeEmailFilter, activeEmailFilterOperator]);

  const handleFilterModelChange = (nextFilterModel) => {
    const supportedFilterItem = (nextFilterModel?.items || []).find(
      (item) => item.field === 'name' || item.field === 'contactEmail'
    );

    const normalizedFilterItem = supportedFilterItem
      ? {
          ...supportedFilterItem,
          operator: supportedFilterItem.operator === 'equals' ? 'equals' : 'startsWith',
          value: supportedFilterItem.value || '',
        }
      : null;

    setFilterModel({
      ...nextFilterModel,
      items: normalizedFilterItem ? [normalizedFilterItem] : [],
    });
  };

  const handleRowUpdate = (hospital) => {
    setError(null);
    setSuccess('');
    setLoading(true);
    const payload = {
      id: hospital.id || hospital.hospitalId,
      name: hospital.name,
      address: hospital.address,
      country: hospital.country,
      contactEmail: hospital.contactEmail,
      updatedBy: user?.uid || null,
    };
    return hospitalService
      .updateHospital(payload)
      .then(() => {
        setReloadKey((k) => k + 1);
        setSuccess('Hospital updated successfully');
      })
      .catch((err) => {
        console.error('updateHospital failed', err);
        setError(err?.message || 'Failed to update hospital');
        return Promise.reject(err);
      })
      .finally(() => setLoading(false));
  };

  const handleRowAdd = (hospital) => {
    setError(null);
    setSuccess('');
    setLoading(true);
    const payload = {
      name: hospital.name,
      address: hospital.address,
      country: hospital.country,
      contactEmail: hospital.contactEmail,
      createdBy: user?.uid || null,
      updatedBy: user?.uid || null,
    };
    return hospitalService
      .createHospital(payload)
      .then(() => {
        setPage(0);
        setReloadKey((k) => k + 1);
        setSuccess('Hospital created successfully');
      })
      .catch((err) => {
        console.error('createHospital failed', err);
        setError(err?.message || 'Failed to create hospital');
        return Promise.reject(err);
      })
      .finally(() => setLoading(false));
  };

  const handleRowDelete = (hospital) => {
    setError(null);
    setSuccess('');
    setLoading(true);
    const id = hospital.id;
    return hospitalService
      .deleteHospital(id)
      .then(() => {
        if (rows.length === 1 && page > 0) {
          setPage((p) => p - 1);
        }
        setReloadKey((k) => k + 1);
        setSuccess('Hospital deleted successfully');
      })
      .catch((err) => {
        console.error('deleteHospital failed', err);
        setError(err?.message || 'Failed to delete hospital');
      })
      .finally(() => setLoading(false));
  };

  const handleRowView = (hospital) => {
    setError(null);
    const id = hospital.id;
    if (!id) return;

    setViewLoading(true);
    hospitalService
      .getHospital(id)
      .then((full) => {
        setViewHospital(full);
        setViewOpen(true);
      })
      .catch((err) => {
        console.error('getHospital for view failed', err);
        setError(err);
      })
      .finally(() => setViewLoading(false));
  };

  useEffect(() => {
    pageCursorSnapshotsRef.current = { 0: null };

    if (page !== 0) {
      pendingPageResetRef.current = true;
      setPage(0);
      return;
    }

    pendingPageResetRef.current = false;
  }, [pageSize, debouncedFilters.nameFilter, debouncedFilters.nameFilterOperator, debouncedFilters.emailFilter, debouncedFilters.emailFilterOperator]);

  const resolvePageCursor = async (targetPage, targetPageSize) => {
    if (targetPage <= 0) return null;

    if (Object.prototype.hasOwnProperty.call(pageCursorSnapshotsRef.current, targetPage)) {
      return pageCursorSnapshotsRef.current[targetPage];
    }

    const knownPages = Object.keys(pageCursorSnapshotsRef.current)
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value >= 0)
      .sort((left, right) => left - right);

    let currentPage = 0;
    let currentCursor = null;

    for (const knownPage of knownPages) {
      if (knownPage < targetPage) {
        currentPage = knownPage;
        currentCursor = pageCursorSnapshotsRef.current[knownPage];
      }
    }

    while (currentPage < targetPage) {
      const response = await hospitalService.listHospitals({
        pageSize: targetPageSize,
        cursorSnapshot: currentCursor,
        includeTotalCount: false,
        nameFilter: debouncedFilters.nameFilter,
        nameFilterOperator: debouncedFilters.nameFilterOperator,
        emailFilter: debouncedFilters.emailFilter,
        emailFilterOperator: debouncedFilters.emailFilterOperator,
      });

      pageCursorSnapshotsRef.current[currentPage + 1] = response.lastVisibleSnapshot;
      currentCursor = response.lastVisibleSnapshot;
      currentPage += 1;

      if (!currentCursor) {
        break;
      }
    }

    return pageCursorSnapshotsRef.current[targetPage] ?? null;
  };

  const fetchPage = async () => {
    if (!authChecked || !user) return;
    const requestId = latestFetchRequestRef.current + 1;
    latestFetchRequestRef.current = requestId;
    setError(null);
    setLoading(true);
    try {
      const currentMonthKey = new Date().toISOString().slice(0, 7);
      const cursorSnapshot = await resolvePageCursor(page, pageSize);
      const data = await hospitalService.listHospitals({
        page,
        pageSize,
        cursorSnapshot,
        includeTotalCount: false,
        nameFilter: debouncedFilters.nameFilter,
        nameFilterOperator: debouncedFilters.nameFilterOperator,
        emailFilter: debouncedFilters.emailFilter,
        emailFilterOperator: debouncedFilters.emailFilterOperator,
      });
      const hits = (data && data.hospitals) || [];

      if (latestFetchRequestRef.current !== requestId) {
        return;
      }

      pageCursorSnapshotsRef.current[page + 1] = data.lastVisibleSnapshot;
      setHasMore(Boolean(data.hasMore));

      setRows(
        hits.map((hospital) => ({
          id: hospital.id,
          name: hospital.name || '',
          address: hospital.address || '',
          country: hospital.country || '',
          contactEmail: hospital.contactEmail || '',
          totalUploads: Number(hospital?.uploadStats?.totalUploads) || 0,
          uploadsThisMonth: Number(hospital?.uploadStats?.uploadsByMonth?.[currentMonthKey]) || 0,
          createdAt: hospital.createdAt || null,
          createdBy: hospital.createdBy || '',
          updatedAt: hospital.updatedAt || null,
          updatedBy: hospital.updatedBy || '',
        }))
      );
    } catch (err) {
      if (latestFetchRequestRef.current !== requestId) {
        return;
      }
      console.error('listHospitals failed', err);
      setError(err);
      setRows([]);
      setHasMore(false);
    } finally {
      if (latestFetchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (pendingPageResetRef.current && page !== 0) {
      return;
    }

    pendingPageResetRef.current = false;
    fetchPage();
  }, [authChecked, page, pageSize, reloadKey, debouncedFilters.nameFilter, debouncedFilters.nameFilterOperator, debouncedFilters.emailFilter, debouncedFilters.emailFilterOperator]);

  if (!authChecked) return null;

  return (
    <AppTheme themeComponents={xThemeComponents} {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <BreadCrumbsWithIcons
            items={[
              { key: 'home', label: 'Home', to: '/dashboard', Icon: HomeOutlinedIcon },
              { key: 'hospitals', label: 'Hospitals', to: null, Icon: LocalHospitalOutlinedIcon },
            ]}
          />
          <NotificationAlert error={error} onClear={() => setError(null)} />
          <NotificationAlert
            severity="success"
            message={success}
            duration={5000}
            onClear={() => setSuccess('')}
          />
          <HospitalsDataGrid
            rows={rows}
            loading={loading}
            page={page}
            pageSize={pageSize}
            hasMore={hasMore}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(ps) => setPageSize(ps)}
            onRowView={handleRowView}
            onRowUpdate={handleRowUpdate}
            onRowAdd={handleRowAdd}
            onRowDelete={handleRowDelete}
          />
          <HospitalViewDialog
            open={viewOpen}
            hospital={viewHospital}
            onClose={() => setViewOpen(false)}
            loading={viewLoading}
          />
        </Box>
      </Box>
    </AppTheme>
  );
}
